import React, { useLayoutEffect, useRef, useState } from 'react';
import Select, { components, createFilter } from 'react-select';
import catalog from './epsgCatalog.json';

const makeOption = system => ({
    value: `EPSG:${system.code}`,
    ahd: system.ahd,
    label: `EPSG:${system.code} — ${system.name}${system.deprecated ? ' (deprecated)' : ''}`
});

export const destinationOptions = catalog.systems.map(makeOption);
export const ahdDestinationCount = destinationOptions.filter(option => option.ahd).length;
export const sourceOptions = catalog.systems.filter(system => system.utm).map(makeOption);
const destinationByValue = new Map(destinationOptions.map(option => [option.value, option]));
const sourceByValue = new Map(sourceOptions.map(option => [option.value, option]));
const canonical = value => value ? `EPSG:${value.trim().replace(/^EPSG:/i, '')}` : '';
const filterOption = createFilter({ ignoreAccents: false });
const ROW_HEIGHT = 36;

// Render only visible rows while keeping every CRS searchable and reachable
// with the keyboard. A plain react-select menu mounts thousands of option nodes.
export function EpsgMenuList(props) {
    const rows = React.Children.toArray(props.children);
    const virtual = rows.length > 50;
    const height = Math.min(props.maxHeight, rows.length * ROW_HEIGHT);
    const focus = rows.findIndex(row => row.props && row.props.data === props.focusedOption);
    const element = useRef(null);
    const [top, setTop] = useState(Math.max(0, focus * ROW_HEIGHT));
    const attachRef = node => {
        element.current = node;
        if (typeof props.innerRef === 'function') props.innerRef(node);
        else if (props.innerRef) props.innerRef.current = node;
    };

    useLayoutEffect(() => {
        if (!virtual || !element.current) return;
        const node = element.current;
        let next = Math.min(node.scrollTop, Math.max(0, rows.length * ROW_HEIGHT - height));
        if (focus >= 0) {
            if (focus * ROW_HEIGHT < next) next = focus * ROW_HEIGHT;
            else if ((focus + 1) * ROW_HEIGHT > next + height) next = (focus + 1) * ROW_HEIGHT - height;
        }
        node.scrollTop = next;
        setTop(next);
    }, [focus, rows.length, height, virtual]);

    if (!virtual) return <components.MenuList {...props} innerRef={attachRef} />;
    const start = Math.max(0, Math.floor(top / ROW_HEIGHT) - 3);
    const end = Math.min(rows.length, Math.ceil((top + height) / ROW_HEIGHT) + 3);
    return <components.MenuList {...props} innerRef={attachRef}
        innerProps={{ ...props.innerProps, onScroll: event => setTop(event.currentTarget.scrollTop) }}>
        <div style={{ height: rows.length * ROW_HEIGHT, position: 'relative' }}>
            {rows.slice(start, end).map((row, index) =>
                <div key={row.key} style={{ position: 'absolute', top: (start + index) * ROW_HEIGHT, height: ROW_HEIGHT, width: '100%' }}>
                    {row}
                </div>)}
        </div>
    </components.MenuList>;
}

const styles = {
    singleValue: base => ({ ...base, color: '#000' }),
    input: base => ({ ...base, color: '#000' }),
    placeholder: base => ({ ...base, color: '#000' }),
    noOptionsMessage: base => ({ ...base, color: '#000' }),
    menu: base => ({ ...base, zIndex: 1060 }),
    menuList: base => ({ ...base, paddingTop: 0, paddingBottom: 0 }),
    option: base => ({ ...base, color: '#000', height: ROW_HEIGHT, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', boxSizing: 'border-box' })
};

export const isCatalogEpsg = (value, source = false) =>
    (source ? sourceByValue : destinationByValue).has(canonical(value));

export default function EpsgSelect({ inputId, value, onChange, source = false, ahd = false }) {
    const code = canonical(value);
    const options = source ? sourceOptions : destinationOptions;
    // Keep saved values visible, including legacy values without the EPSG prefix.
    // An unavailable value is never silently replaced by a different CRS.
    const selected = destinationByValue.get(code) || (code ? { value: code, label: `${code} (unavailable)` } : null);
    return <Select inputId={inputId} instanceId={inputId} options={options} value={selected}
        onChange={option => onChange(option ? option.value : '')}
        isOptionDisabled={option => ahd && !option.ahd}
        isSearchable isClearable menuPlacement="auto" maxMenuHeight={252}
        placeholder="Search EPSG code or coordinate system…"
        noOptionsMessage={() => 'No matching coordinate systems'}
        components={{ MenuList: EpsgMenuList }} styles={styles} filterOption={filterOption}
        formatOptionLabel={option => <span title={option.label} style={{ color: '#000' }}>{option.label}{ahd && !option.ahd ? ' (AHD unavailable for geocentric XYZ)' : ''}</span>} />;
}
